import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

import {
  getVendorBankAccounts,
  createVendorBankAccount,
  getCurrencies,
  setDefaultVendorBankAccount,
} from "../../api/vendorBankAccounts";
import { usePermissions } from "../../hooks/usePermissions";
import { validateIBAN } from "../../utils/validateIbanNumber";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  open: boolean;
  onClose: () => void;
  companyId: string;
  vendorId: string;
};

type CurrencyOption = {
  code: string;
  description?: string;
};

type VendorBankAccount = {
  id: string;
  iban?: string;
  bankAccountNo: string;
  currencyCode?: string;
  default: boolean;
};

export default function VendorBankAccountsModal({
  open,
  onClose,
  companyId,
  vendorId,
}: Props) {
  const { t } = useAppTranslation("common");
  const { canManageBankAccounts } = usePermissions();

  const [accounts, setAccounts] = React.useState<VendorBankAccount[]>([]);
  const [loading, setLoading] = React.useState(false);

  const [iban, setIban] = React.useState("");
  const [accountNo, setAccountNo] = React.useState("");
  const [isDefault, setIsDefault] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  const [createError, setCreateError] = React.useState<string | null>(null);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = React.useState(false);

  const [currencies, setCurrencies] = React.useState<CurrencyOption[]>([]);
  const [selectedCurrency, setSelectedCurrency] = React.useState("PLN");
  const [activeCurrency, setActiveCurrency] = React.useState("PLN");

  const [loadingDefaultId, setLoadingDefaultId] = React.useState<string | null>(
    null
  );

  const hasCountryCode = /^[A-Z]{2}/.test(iban);
  const isValidIban = validateIBAN(iban) && hasCountryCode;

  const isBankDataInvalid =
    (!!iban && !isValidIban) ||
    (!iban && !!accountNo) ||
    (!!iban && !accountNo);

  const isCreateDisabled =
    creating || !iban.trim() || !accountNo.trim() || isBankDataInvalid;

  const resetCreateForm = React.useCallback(() => {
    setIban("");
    setAccountNo("");
    setIsDefault(false);
    setSelectedCurrency("PLN");
    setCreateError(null);
  }, []);

  const handleClose = () => {
    setCreateError(null);
    setSuccessSnackbarOpen(false);
    resetCreateForm();
    onClose();
  };

  const handleSnackbarClose = (
    _: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSuccessSnackbarOpen(false);
  };

  const loadAccounts = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVendorBankAccounts(companyId, vendorId);
      const mapped = data.map((a: VendorBankAccount) => ({
        ...a,
        currencyCode: mapCurrencyFromApi(a.currencyCode),
      }));
      setAccounts(mapped);
    } finally {
      setLoading(false);
    }
  }, [companyId, vendorId]);

  React.useEffect(() => {
    if (!open) return;

    setCreateError(null);
    setSuccessSnackbarOpen(false);

    loadAccounts();
  }, [open, loadAccounts]);

  React.useEffect(() => {
    if (!open) return;

    getCurrencies(companyId)
      .then((data) => setCurrencies(data || []))
      .catch(() => setCurrencies([]));
  }, [open, companyId]);

  const handleCreate = async () => {
    if (isCreateDisabled) return;

    setCreating(true);
    setCreateError(null);

    try {
      await createVendorBankAccount(companyId, vendorId, {
        iban: iban.trim(),
        bankAccountNo: accountNo.trim(),
        currencyCode: mapCurrencyToApi(selectedCurrency),
        default: isDefault,
      });

      await loadAccounts();

      const createdCurrency = selectedCurrency;

      resetCreateForm();
      setActiveCurrency(createdCurrency);
      setSuccessSnackbarOpen(true);
    } catch (e: any) {
      setCreateError(
        e?.message || "Nie udało się dodać konta bankowego."
      );
    } finally {
      setCreating(false);
    }
  };

  const groupedAccounts = React.useMemo(() => {
    const map: Record<string, VendorBankAccount[]> = {};

    for (const acc of accounts) {
      const currency = acc.currencyCode || "PLN";

      if (!map[currency]) {
        map[currency] = [];
      }

      map[currency].push(acc);
    }

    return map;
  }, [accounts]);

  const accountCurrencies = React.useMemo(() => {
    return Object.keys(groupedAccounts);
  }, [groupedAccounts]);

  React.useEffect(() => {
    if (accountCurrencies.length === 0) {
      setActiveCurrency("PLN");
    } else if (!accountCurrencies.includes(activeCurrency)) {
      setActiveCurrency(accountCurrencies[0]);
    }
  }, [accountCurrencies, activeCurrency]);

  const visibleAccounts = groupedAccounts[activeCurrency] || [];

  const handleSetDefault = async (accountId: string, isDefaultValue: boolean) => {
    if (isDefaultValue || loadingDefaultId) return;

    setLoadingDefaultId(accountId);

    try {
      await setDefaultVendorBankAccount(companyId, vendorId, accountId);
      await loadAccounts();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDefaultId(null);
    }
  };

  function mapCurrencyFromApi(code?: string) {
    return code || "PLN";
  }

  function mapCurrencyToApi(code: string) {
    return code === "PLN" ? "" : code;
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t("Zarządzanie kontami bankowymi")}</DialogTitle>

        <DialogContent>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              {canManageBankAccounts && (
                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1">
                    {t("Dodaj konto bankowe")}
                  </Typography>

                  {createError && (
                    <Alert
                      severity="error"
                      onClose={() => setCreateError(null)}
                    >
                      {createError}
                    </Alert>
                  )}

                  <TextField
                    label="IBAN"
                    value={iban}
                    onChange={(e) => {
                      setIban(e.target.value.toUpperCase());
                      setCreateError(null);
                    }}
                    size="small"
                    fullWidth
                    required
                    disabled={creating}
                    error={iban.length > 0 && !isValidIban}
                    helperText={
                      iban.length > 0 && !isValidIban
                        ? t("Nieprawidłowy IBAN (błąd checksum lub kod kraju)")
                        : ""
                    }
                  />

                  <TextField
                    label={t("Numer konta")}
                    value={accountNo}
                    onChange={(e) => {
                      setAccountNo(e.target.value);
                      setCreateError(null);
                    }}
                    size="small"
                    fullWidth
                    required
                    disabled={creating}
                  />

                  <Autocomplete
                    options={currencies}
                    getOptionLabel={(o) => o?.code || ""}
                    value={
                      currencies.find((c) => c.code === selectedCurrency) || null
                    }
                    onChange={(_, v) => setSelectedCurrency(v?.code || "PLN")}
                    disabled={creating}
                    renderInput={(params) => (
                      <TextField {...params} label={t("Waluta")} size="small" />
                    )}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                        disabled={creating}
                      />
                    }
                    label={t("Ustaw jako domyślne")}
                  />

                  {isBankDataInvalid && (
                    <Alert severity="error">
                      {t("IBAN musi być poprawny oraz oba pola muszą być uzupełnione.")}
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={isCreateDisabled}
                    startIcon={
                      creating ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {creating ? t("Dodawanie...") : t("Dodaj konto")}
                  </Button>

                  <Divider />
                </Stack>
              )}

              {accountCurrencies.length > 0 && (
                <Tabs
                  value={activeCurrency}
                  onChange={(_, v) => setActiveCurrency(v)}
                  sx={{ mb: 2 }}
                >
                  {accountCurrencies.map((c) => (
                    <Tab
                      key={c}
                      label={`${c} (${groupedAccounts[c].length})`}
                      value={c}
                    />
                  ))}
                </Tabs>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {t("Kliknij „Ustaw jako domyślne”, aby zmienić konto dla tej waluty")}
              </Typography>

              <Stack spacing={0}>
                {visibleAccounts.map((acc) => (
                  <Stack
                    key={acc.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      py: 1.5,
                      px: 1,
                      backgroundColor: acc.default
                        ? "action.selected"
                        : "transparent",
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        {acc.bankAccountNo}
                      </Typography>

                      {acc.iban && (
                        <Typography variant="caption" color="text.secondary">
                          IBAN: {acc.iban}
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {t("Waluta")}: {acc.currencyCode || "PLN"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center">
                      {acc.default ? (
                        <Chip
                          icon={<StarIcon />}
                          label={t("Domyślne")}
                          color="primary"
                          size="small"
                        />
                      ) : loadingDefaultId === acc.id ? (
                        <CircularProgress size={16} />
                      ) : canManageBankAccounts ? (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => handleSetDefault(acc.id, acc.default)}
                        >
                          {t("Ustaw jako domyślne")}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                ))}

                {visibleAccounts.length === 0 && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {t("Brak kont dla waluty")} {activeCurrency}
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" disabled={creating}>
            {t("Zamknij")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {t("Konto bankowe zostało dodane.")}
        </Alert>
      </Snackbar>
    </>
  );
}